import React, {
    useState,
    useEffect,
    Dispatch,
    SetStateAction,
    SyntheticEvent,
    useCallback,
} from 'react';
import MatchStatFormFrame from './MatchStatFormFrame';
import { MatchAdmin, MatchEventRecordingOption } from '../../../models/interfaces/Match';
import {
    MatchStatFormValues,
    HandleSubmitNewStatProps,
    HandleSubmitSelectedStatProps,
    ToastDetails,
} from '../../interfaces/MatchUI';
import { MatchSegmentStats, Stat } from '../../../models/interfaces/Stat';
import DisplaySingleStat from './DisplaySingleStat';
import { toastColorMap } from '../../../helpers/toastColorMap';
import { Toast } from 'react-bootstrap';
import { RosterEntry } from '../../../models/interfaces/RosterEntry';

interface Props {
    matchAdmin: MatchAdmin;
    editingStat: Stat | undefined;
    setEditingStat: Dispatch<SetStateAction<Stat | undefined>>;
    tabSelected: boolean;
    triggerUpdatedMatchStats: () => void;
    lightCapPlayerList: RosterEntry[] | undefined;
    darkCapPlayerList: RosterEntry[] | undefined;
    existingStats: MatchSegmentStats[] | undefined;
}

const ModalManageStatDisplay = ({
    matchAdmin,
    editingStat,
    setEditingStat,
    tabSelected,
    triggerUpdatedMatchStats,
    lightCapPlayerList,
    darkCapPlayerList,
    existingStats
}: Props) => {
    const [toastDetails, setToastDetails] = useState<ToastDetails>({
        showToast: false,
        toastText: null,
        status: null,
    });
    const [lastFormValues, setLastFormValues] =
        useState<MatchStatFormValues | undefined>(undefined);
    const [lastStatInfo, setLastStatInfo] = useState< { lastStat: Stat, text: string }| undefined>(undefined);

    const handleEditStat = useCallback(() => {
        if (!editingStat) {
            // will cause editingStat to be updated with the last stat which will cause a re-render & subsequently call handleEditStat(see next useEffect) with values for editingStat
            setEditingStat(lastStatInfo?.lastStat);
        } else {
            let recordingOptions = matchAdmin.recordingOptionGroups.reduce((currentRecordingOptions, recordingGroup) => {
                return currentRecordingOptions.concat(recordingGroup.recordingOptions)
            }, new Array<MatchEventRecordingOption>())
            let foundStat = recordingOptions.find((option) => {
                return option.typeDescription.matchesDescription(editingStat.description);
            });
            setLastFormValues({
                selectedTeam: editingStat.team ? editingStat.team : '',
                selectedPlayer: editingStat.player ? editingStat.player.id : '',
                selectedPeriod: editingStat.matchSegment.databaseValue,
                selectedMinutes: Math.floor(editingStat.timestamp / 60),
                selectedSeconds: editingStat.timestamp % 60,
                selectedStat: foundStat ? foundStat : '',
                selectedRemark: editingStat.remarks ?? '',
            });
        }
    }, [editingStat, matchAdmin.recordingOptionGroups, lastStatInfo, setEditingStat]);

    // needed for when a user comes from timeline view to go straight into edit mode
    useEffect(() => {
        if (editingStat) {
            handleEditStat();
        }
    }, [editingStat, handleEditStat]);

    const handleSubmitNewStat = ({
        matchSegment,
        timestamp,
        performer,
        selectedStatDescription,
        remarks,
        resetForm,
        setSubmitting,
    }: HandleSubmitNewStatProps) => {
        matchAdmin
            .addStat(matchSegment, timestamp, performer, selectedStatDescription, remarks)
            .then((returnedStat) => {
                setEditingStat(undefined);
                resetForm();
                const existingStatsForMatch = existingStats ? existingStats.flatMap(matchSegmentStats => {
                     return matchSegmentStats.stats
                }) : []
                return matchAdmin.displayTextForAddedStat(returnedStat, existingStatsForMatch).then(displayText => {
                    setLastStatInfo({ lastStat: returnedStat, text: displayText.text })
                    setToastDetails((details) => ({
                        ...details,
                        showToast: true,
                        toastText: displayText.text,
                        status: displayText.category,
                    }))
                });
            })
            .catch((error) => {
                console.error('stat not added ' + error);
                setToastDetails((details) => ({
                    ...details,
                    showToast: true,
                    toastText: 'An error occurred, saving the stat',
                    status: 'failed',
                }));
                setSubmitting(false);
            });
    };

    const handleSubmitSelectedStat = ({
        stat,
        matchSegment,
        timestamp,
        performer,
        selectedStatDescription,
        remarks,
        resetForm,
        setSubmitting,
    }: HandleSubmitSelectedStatProps) => {
        matchAdmin
            .updateStat(stat, matchSegment, timestamp, performer, selectedStatDescription, remarks)
            .then((returnedStat) => {
                setLastStatInfo({ lastStat: returnedStat, text: 'Update Successful' });
                setEditingStat(undefined);
                resetForm();
                setToastDetails((details) => ({
                    ...details,
                    showToast: true,
                    toastText: 'Update Successful',
                    status: 'success',
                }));
                triggerUpdatedMatchStats();
            })
            .catch((error) => {
                console.error('stat not added' + error);
                setToastDetails((details) => ({
                    ...details,
                    showToast: true,
                    toastText: 'An error occurred, saving the stat',
                    status: 'failed',
                }));
                setSubmitting(false);
            });
    };

    const handleClearForm = ({
        handleReset,
    }: {
        handleReset: (e?: SyntheticEvent<any, Event> | undefined) => void;
    }) => {
        setEditingStat(undefined);
        handleReset();
    };

    const handleDeleteStat = useCallback(
        (stat: Stat, resetState: () => void) => {
            matchAdmin
                .removeStat(stat)
                .catch((e) => console.error(e))
                .finally(() => {
                    resetState();
                    setLastStatInfo(undefined);
                });
        },
        [matchAdmin, setLastStatInfo]
    );

    return (
        <>
            {!editingStat && (
                <DisplaySingleStat
                    lastStat={lastStatInfo?.lastStat}
                    handleEditStat={handleEditStat}
                    handleDeleteStat={handleDeleteStat}
                    titleString={lastStatInfo?.text}
                    editMatchStats={true}
                    showUpdateTime={false}
                />
            )}
            {!!editingStat ? (
                <MatchStatFormFrame
                    matchAdmin={matchAdmin}
                    editValues={lastFormValues}
                    editingStat={editingStat}
                    handleSubmitEditingStat={handleSubmitSelectedStat}
                    handleClearForm={handleClearForm}
                    setToastDetails={setToastDetails}
                    tabSelected={tabSelected}
                    lightCapPlayerList={lightCapPlayerList}
                    darkCapPlayerList={darkCapPlayerList}
                />
            ) : (
                <MatchStatFormFrame
                    matchAdmin={matchAdmin}
                    handleSubmitNewStat={handleSubmitNewStat}
                    handleClearForm={handleClearForm}
                    lastStatPeriod={lastStatInfo?.lastStat ? lastStatInfo.lastStat.matchSegment.databaseValue : undefined}
                    setToastDetails={setToastDetails}
                    tabSelected={tabSelected}
                    lightCapPlayerList={lightCapPlayerList}
                    darkCapPlayerList={darkCapPlayerList}
                />
            )}
            <Toast
                onClose={() =>
                    setToastDetails({
                        showToast: false,
                        toastText: null,
                        status: null,
                    })
                }
                show={toastDetails.showToast}
                delay={3000}
                autohide
                style={{
                    position: 'absolute',
                    bottom: '5rem',
                    right: 0,
                    backgroundColor: toastColorMap.get(toastDetails.status) || 'transparent',
                }}
            >
                <Toast.Body>{toastDetails.toastText}</Toast.Body>
            </Toast>
        </>
    );
};

export default ModalManageStatDisplay;
